import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, useMotionValueEvent, useScroll, useTransform } from 'framer-motion';

const aboutCopy =
  'I’m Jabez Llave. I build full-stack products that feel effortless and solve real problems.';

const highlights = [
  { label: 'Age', value: '18' },
  { label: 'Current focus', value: 'Full-stack apps + AI systems' },
  { label: 'Experience', value: '4 years building web and mobile products' },
  { label: 'Based in', value: 'Caloocan, Philippines' },
];

const skills = [
  'React',
  'Next.js',
  'React Native',
  'Node.js',
  'Python',
  'MongoDB',
  'PostgreSQL',
  'Socket.io',
  'WebRTC',
  'AI integrations',
];

const workHistory = [
  {
    role: 'App Developer / AI Engineer',
    company: 'Ezzogenics',
    location: 'Singapore',
    period: '2025 — Present',
    logo: '/logos/ezzogenics.jpg',
    detail: 'Building AI-enabled full-stack applications, scalable APIs, responsive product interfaces, and cloud deployments for enterprise workflows.',
  },
  {
    role: 'Founder & Developer',
    company: 'Duabooth',
    period: '2025',
    logo: '/duabooth.png',
    detail: 'Created a realtime photobooth experience with WebRTC, synchronized captures, and cross-platform sharing that reached 50,000 users.',
  },
  {
    role: 'Web Developer',
    company: 'SaveMySlip.com',
    period: '2025',
    logo: '/savemyslip.png',
    detail: 'Delivered a production boat-slip reservation platform with authentication, admin controls, reporting, and reservation rule enforcement.',
  },
];

const Section = styled.section`
  padding: clamp(110px, 10vw, 170px) 24px clamp(88px, 10vw, 132px);
  background: ${({ theme }) => theme.colors.background};

  @media (max-width: 780px) {
    padding: 72px 20px 64px;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 780px) {
    max-width: 560px;
  }
`;

const Heading = styled(motion.h2)`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2.55rem, 4.2vw, 3.75rem);
  line-height: 1;
  letter-spacing: -0.07em;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.ink};
  margin: 0 0 clamp(34px, 5vw, 52px);

  @media (max-width: 780px) {
    margin-bottom: 30px;
    letter-spacing: -0.06em;
  }
`;

const RevealIntro = styled(motion.div)`
  width: min(1120px, 100%);
  margin: 0 auto clamp(86px, 9vw, 124px);
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: clamp(3.2rem, 5.35vw, 5.6rem);
  line-height: 1.13;
  letter-spacing: -0.078em;
  font-weight: 400;
  color: #d3d3d5;

  @media (max-width: 780px) {
    width: 100%;
    padding: 0 50px;
    margin-bottom: 76px;
    font-size: clamp(2.85rem, 8.4vw, 4.7rem);
    line-height: 1.16;
    letter-spacing: -0.075em;
  }

  @media (max-width: 430px) {
    padding: 0 22px;
    font-size: clamp(2.2rem, 12.8vw, 3.55rem);
  }
`;

const RevealWord = styled.span`
  display: inline-block;
  color: ${({ $active, theme }) => ($active ? theme.colors.ink : '#d3d3d5')};
  transition: color 0.12s linear;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 668px) minmax(260px, 330px);
  gap: clamp(42px, 7vw, 96px);
  align-items: start;
  justify-content: space-between;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr) minmax(210px, 30vw);
    gap: 34px;
  }

  /* Must match MetaPanel's collapse point, or the two grids disagree on
     column structure between the breakpoints and the page loses its rhythm. */
  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 26px;
  }
`;

const Copy = styled(motion.div)`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: clamp(1.25rem, 2.1vw, 1.5rem);
  line-height: 1.4;
  letter-spacing: -0.03em;
  font-weight: 400;
  color: transparent;

  p {
    margin: 0 0 21px;
    background: linear-gradient(
      180deg,
      ${({ theme }) => theme.colors.ink} var(--text-fill, 0%),
      #d7d7d7 var(--text-fill, 0%)
    );
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  p:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 780px) {
    font-size: clamp(1.12rem, 5vw, 1.34rem);
    line-height: 1.42;
    letter-spacing: -0.025em;
  }
`;

const VisualColumn = styled(motion.aside)`
  position: sticky;
  top: 108px;
  padding-top: 2px;

  @media (max-width: 820px) {
    position: relative;
    top: auto;
    /* Full content width so the photo sits on the same left edge as the copy. */
    width: 100%;
    margin: 0;
    opacity: 1;
  }
`;

const PhotoCard = styled.div`
  position: relative;
  aspect-ratio: 0.74;
  border-radius: 24px;
  overflow: hidden;
  background: linear-gradient(145deg, #f4f4f4 0%, #e8e8e8 100%);
  box-shadow: 0 18px 45px rgba(20, 20, 20, 0.08);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(255,255,255,0.72), transparent 42%);
    z-index: 1;
    pointer-events: none;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  @media (max-width: 820px) {
    /* Landscape crop keeps the photo from eating half the screen. The source
       is already 3:2, so cover alone would show the full cluttered frame —
       scale in on the face instead. Subject sits at ~50%/46%. */
    aspect-ratio: 1.5;
    border-radius: 20px;

    img {
      transform: scale(1.45);
      transform-origin: 50% 46%;
    }

    /* The left wash was composed for the tall crop; it swamps the wide one. */
    &::before {
      background: linear-gradient(90deg, rgba(255, 255, 255, 0.34), transparent 34%);
    }
  }
`;

const MetaPanel = styled(motion.div)`
  margin-top: clamp(74px, 9vw, 112px);
  display: grid;
  grid-template-columns: minmax(0, 668px) minmax(260px, 330px);
  gap: clamp(42px, 7vw, 96px);
  align-items: start;
  justify-content: space-between;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 26px;
    margin-top: 44px;
  }
`;

const ContactCard = styled.div`
  background: #f1f8ff;
  border-radius: 28px;
  padding: 28px 36px 32px;
  min-height: 132px;

  @media (max-width: 520px) {
    padding: 24px 22px 26px;
    border-radius: 24px;
  }
`;

const ContactTitle = styled.h3`
  font-size: clamp(1.2rem, 2vw, 1.45rem);
  line-height: 1.18;
  letter-spacing: -0.035em;
  color: ${({ theme }) => theme.colors.ink};
  margin: 0 0 20px;
`;

const ContactControls = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;

  /* Below this the two pills can't share a row, and wrapping leaves a ragged
     half-row. Stack them full width so it reads as a deliberate CTA pair. */
  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;
    gap: 9px;
  }
`;

const EmailPill = styled.a`
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  gap: 14px;
  padding: 0 18px;
  border-radius: 999px;
  background: #dcecff;
  color: ${({ theme }) => theme.colors.ink};
  font-size: 0.95rem;
  line-height: 1;
  text-decoration: none;

  @media (max-width: 520px) {
    justify-content: center;
  }
`;

const BookButton = styled.a`
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 0 22px;
  border-radius: 999px;
  background: #2f80ff;
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  line-height: 1;
  text-decoration: none;
  box-shadow: 0 8px 20px rgba(47, 128, 255, 0.28);
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding-top: 18px;

  @media (max-width: 820px) {
    padding-top: 0;
  }
`;

const SmallTitle = styled.h3`
  font-size: 1.25rem;
  line-height: 1.2;
  letter-spacing: -0.03em;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink};
  margin: 0;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 13px;
  border-radius: 999px;
  background: #f4f4f4;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: 0.88rem;
`;

const WorkList = styled.div`
  margin-top: clamp(70px, 9vw, 104px);
  display: flex;
  flex-direction: column;
  gap: 29px;

  @media (max-width: 820px) {
    margin-top: 52px;
    gap: 22px;
  }
`;

const WorkHeading = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(2.05rem, 3.2vw, 2.7rem);
  line-height: 1.02;
  letter-spacing: -0.06em;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.ink};
  margin: 0 0 26px;
`;

const WorkItem = styled(motion.article)`
  display: grid;
  grid-template-columns: minmax(270px, 0.72fr) minmax(0, 1.28fr);
  gap: clamp(28px, 4.2vw, 58px);
  align-items: start;
  color: ${({ theme }) => theme.colors.ink};

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const WorkIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  min-width: 0;
`;

const WorkLogo = styled.div`
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
  border-radius: 14px;
  overflow: hidden;
  background: #f1f6fb;
  box-shadow: inset 0 0 0 1px rgba(10, 10, 10, 0.05);

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const WorkText = styled.div`
  min-width: 0;
`;

const WorkRole = styled.h3`
  margin: 0 0 4px;
  font-size: 1.02rem;
  line-height: 1.22;
  letter-spacing: -0.03em;
  font-weight: 700;
`;

const WorkMeta = styled.div`
  color: ${({ theme }) => theme.colors.inkMute};
  font-size: 0.91rem;
  line-height: 1.45;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WorkDetail = styled.p`
  margin: 0;
  max-width: 690px;
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: 1.03rem;
  line-height: 1.55;
  letter-spacing: -0.018em;
`;

const About = () => {
  const introRef = useRef(null);
  const bodyCopyRef = useRef(null);
  const [activeWordCount, setActiveWordCount] = useState(0);
  const { scrollYProgress } = useScroll({
    target: introRef,
    offset: ['start 78%', 'end 34%'],
  });
  const { scrollYProgress: bodyCopyProgress } = useScroll({
    target: bodyCopyRef,
    offset: ['start 78%', 'end 42%'],
  });
  const words = useMemo(() => aboutCopy.split(' '), []);
  const revealCount = useTransform(scrollYProgress, [0, 1], [0, words.length]);
  const bodyTextFill = useTransform(bodyCopyProgress, [0, 1], ['0%', '100%']);

  useMotionValueEvent(revealCount, 'change', (latest) => {
    setActiveWordCount(Math.max(0, Math.min(words.length, Math.round(latest))));
  });

  return (
    <Section id="about">
      <Container>
        <RevealIntro ref={introRef}>
          {words.map((word, index) => (
            <RevealWord
              key={`${word}-${index}`}
              $active={index < activeWordCount}
            >
              {word}&nbsp;
            </RevealWord>
          ))}
        </RevealIntro>

        <Heading
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
        >
          About me
        </Heading>

        <Body>
          <Copy
            ref={bodyCopyRef}
            style={{ '--text-fill': bodyTextFill }}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, margin: '-80px' }}
          >
            {[
              'I build full-stack products that connect clean interfaces with reliable systems. My work spans React, Next.js, React Native, Node.js, Python, databases, and AI integrations — the pieces needed to turn an idea into a working product.',
              'You get one developer who can own the build from prototype to release. I can plan the API, design the interface, wire the database, integrate AI, deploy the system, and keep refining it after launch.',
            ].map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </Copy>

          <VisualColumn
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, margin: '-80px' }}
          >
            <PhotoCard>
              <img src="/jabez.jpg" alt="Jabez Llave" loading="lazy" />
            </PhotoCard>
          </VisualColumn>
        </Body>

        <MetaPanel
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: '-80px' }}
        >
          <ContactCard>
            <ContactTitle>Tell me what you're building. Let’s see if it’s a fit.</ContactTitle>
            <ContactControls>
              <EmailPill href="mailto:jabezllave@gmail.com">jabezllave@gmail.com</EmailPill>
              <BookButton href="mailto:jabezllave@gmail.com?subject=Project%20Inquiry">
                Book a Call
                <span aria-hidden="true">→</span>
              </BookButton>
            </ContactControls>
          </ContactCard>

          <Sidebar>
            <SmallTitle>Quick profile</SmallTitle>
            <Tags>
              {highlights.map((item) => (
                <Tag key={item.label}>{item.label}: {item.value}</Tag>
              ))}
            </Tags>
            <SmallTitle>Stack</SmallTitle>
            <Tags>
              {skills.map((skill) => (
                <Tag key={skill}>{skill}</Tag>
              ))}
            </Tags>
          </Sidebar>
        </MetaPanel>

        <WorkList aria-label="Recent experience">
          <WorkHeading>Experience</WorkHeading>
          {workHistory.map((item, index) => (
            <WorkItem
              key={item.company}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true, margin: '-80px' }}
            >
              <WorkIdentity>
                <WorkLogo aria-hidden="true">
                  <img src={item.logo} alt="" loading="lazy" />
                </WorkLogo>
                <WorkText>
                  <WorkRole>{item.role}</WorkRole>
                  <WorkMeta>
                    {item.company}{item.location ? `, ${item.location}` : ''} · {item.period}
                  </WorkMeta>
                </WorkText>
              </WorkIdentity>
              <WorkDetail>{item.detail}</WorkDetail>
            </WorkItem>
          ))}
        </WorkList>
      </Container>
    </Section>
  );
};

export default About;
